import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Card,
    CardHeader,
    CardContent,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Autocomplete,
    Chip,
    FormHelperText
} from '@mui/material';
import {
    Save as SaveIcon,
    LocationCity as CityIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { regionService } from '../../../services/regionService';
import locationService from '../../../services/locationService';

const RegionForm = ({ region, onSuccess, onCancel, viewMode = false }) => {
    const { handleApiCallWithLoading, showError } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [availableCities, setAvailableCities] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        assignedCities: []
    });
    const [errors, setErrors] = useState({});
    const [validationErrors, setValidationErrors] = useState([]);

    useEffect(() => {
        if (region) {
            loadAvailableCities(region.id);
            setFormData({
                name: region.name || '',
                code: region.code || '',
                assignedCities: region.cities || []
            });
        } else {
            loadAvailableCities();
        }
    }, [region]);

    const loadAvailableCities = async (regionId = null) => {
        const apiCall = regionId 
            ? regionService.getAvailableCities(regionId)
            : regionService.getUnassignedCities();

        await handleApiCallWithLoading(
            apiCall,
            setCitiesLoading,
            {
                showSuccess: false,
                errorMessage: 'Failed to load available cities',
                onSuccess: (response) => {
                    setAvailableCities(response.data);
                }
            }
        );
    };

    const validateCityAssignments = async (cityIds, regionId) => {
        try {
            const response = await regionService.validateCityAssignments(cityIds, regionId);
            if (!response.success) {
                setValidationErrors(response.errors);
                return false;
            }
            setValidationErrors([]);
            return true;
        } catch (error) {
            console.error('Error validating city assignments:', error);
            setValidationErrors(['Failed to validate city assignments']);
            return false;
        }
    };

    const handleCityChange = async (newValue) => {
        const cityIds = newValue.map(city => city.id || city.city_id);
        const isValid = await validateCityAssignments(cityIds, region?.id);
        
        if (isValid) {
            handleInputChange('assignedCities', newValue);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Region name is required';
        } else if (formData.name.length > 100) {
            newErrors.name = 'Region name cannot exceed 100 characters';
        }
        
        if (formData.code && formData.code.length > 10) {
            newErrors.code = 'Region code cannot exceed 10 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        const regionData = {
            name: formData.name.trim(),
            code: formData.code.trim() || null
        };

        const submitRegion = async () => {
            let savedRegion;
            if (region) {
                const response = await regionService.updateRegion(region.id, regionData);
                savedRegion = response.data;
            } else {
                const response = await regionService.createRegion(regionData);
                savedRegion = response.data;
            }

            // Now assign cities to the region
            if (formData.assignedCities.length > 0) {
                const cityIds = formData.assignedCities.map(city => city.id || city.city_id);
                await regionService.assignCities(savedRegion.id, cityIds);
            }

            return savedRegion;
        };

        await handleApiCallWithLoading(
            submitRegion(),
            setLoading,
            {
                successMessage: `Region ${region ? 'updated' : 'created'} successfully`,
                errorMessage: 'Operation failed. Please try again.',
                onSuccess: () => {
                    onSuccess();
                },
                onError: (error) => {
                    // Handle validation errors
                    if (error.errors) {
                        const newErrors = {};
                        Object.keys(error.errors).forEach(field => {
                            newErrors[field] = error.errors[field][0];
                        });
                        setErrors(newErrors);
                    }
                }
            }
        );
    };

    return (
        <Box>
            {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Please fix the following issues:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {validationErrors.map((error, index) => (
                            <li key={index}>
                                <Typography variant="body2">
                                    {error}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <Card>
                <CardHeader title="Region Information" />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Region Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name}
                                disabled={viewMode}
                                required
                                placeholder="Enter region name"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Region Code"
                                value={formData.code}
                                onChange={(e) => handleInputChange('code', e.target.value)}
                                error={!!errors.code}
                                helperText={errors.code}
                                disabled={viewMode}
                                placeholder="Optional (e.g., NI, SI)"
                            />
                        </Grid>
                        {!viewMode && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Assign Cities to Region
                                </Typography>
                                <Autocomplete
                                    multiple
                                    options={availableCities}
                                    getOptionLabel={(city) => `${city.name} (${city.state?.code || 'No State'})`}
                                    value={formData.assignedCities}
                                    onChange={(event, newValue) => handleCityChange(newValue)}
                                    loading={citiesLoading}
                                    disabled={viewMode}
                                    filterOptions={(options, { inputValue }) => {
                                        // Custom filter for better search experience with large city lists
                                        const filtered = options.filter(option => {
                                            const cityName = option.name.toLowerCase();
                                            const stateName = option.state?.name?.toLowerCase() || '';
                                            const stateCode = option.state?.code?.toLowerCase() || '';
                                            const search = inputValue.toLowerCase();
                                            
                                            return cityName.includes(search) || 
                                                   stateName.includes(search) || 
                                                   stateCode.includes(search);
                                        });
                                        return filtered;
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Cities"
                                            placeholder="Search by city name, state name, or state code"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {citiesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((city, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={`${city.name} (${city.state?.code || 'No State'})`}
                                                icon={<CityIcon />}
                                                {...getTagProps({ index })}
                                                key={city.id || city.city_id}
                                            />
                                        ))
                                    }
                                    renderOption={(props, city) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">
                                                    {city.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {city.state?.code || 'No State'} • {city.code || 'No Code'}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    noOptionsText="No available cities found"
                                />
                                <FormHelperText>
                                    {region ? 
                                        'Cities that are currently unassigned or assigned to this region are shown here. Each city can belong to only one region at a time.' :
                                        'All unassigned cities are shown here. Each city can belong to only one region at a time.'
                                    }
                                </FormHelperText>
                            </Grid>
                        )}
                        {viewMode && formData.assignedCities.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Cities in this Region
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {formData.assignedCities.map((city) => (
                                        <Chip
                                            key={city.id || city.city_id}
                                            label={city.name}
                                            icon={<CityIcon />}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            {!viewMode && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {region ? 'Update Region' : 'Create Region'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default RegionForm; 